import React from 'react';

import { ChatSurfaceWidget } from '@/components/chat/chat-surface';
import {
  ConfirmationWidgetData,
  ShortcutWidgetData,
} from '@/types/assistant';
import {
  INTERVIEW_WIDGET,
  ONBOARDING_SLOT,
} from '@/types/onboarding-interview';
import { AddressWidget } from './address-widget';
import { AdjustMenuWidget } from './adjust-menu-widget';
import { ChoiceWidget } from './choice-widget';
import { ClientPreviewWidget } from './client-preview-widget';
import { InfoListWidget } from './info-list-widget';
import { ServicePickerWidget } from './service-picker-widget';
import { ConfirmationWidget } from './confirmation-widget';
import { ShortcutWidget } from './shortcut-widget';
import { TextWidget } from './text-widget';
import { LocationWidget } from './location-widget';
import { ServiceListWidget } from './service-list-widget';
import { SubscriptionCardWidget } from './subscription-card-widget';
import { WeeklyGridWidget } from './weekly-grid-widget';
import {
  AddressWidgetData,
  AdjustMenuWidgetData,
  ChoiceWidgetData,
  TextWidgetData,
  ClientPreviewWidgetData,
  InfoListWidgetData,
  LocationWidgetData,
  ServiceListWidgetData,
  ServicePickerWidgetData,
  SubscriptionCardWidgetData,
  WeeklyGridWidgetData,
} from './widget-types';

interface Props {
  widget: ChatSurfaceWidget;
  /** "Ajustar esta parte": o menu de ajuste e o cartão do fecho usam. */
  onAdjust?: (
    input: { slot?: ONBOARDING_SLOT; done?: boolean },
    echo?: string,
  ) => void;
  /** Só o cartão do último turno aceita toque — editar o de cima confunde. */
  editable?: boolean;
  /** "Pronto": respondeu por toque, a conversa anda. Só a entrevista passa. */
  onAdvance?: (echo?: string) => void;
}

/**
 * Despacha o cartão pelo tipo.
 *
 * **Tipo desconhecido é ignorado em silêncio**: um backend mais novo que o app
 * instalado não pode quebrar a conversa por causa de um widget. O texto da
 * fala continua lá e a entrevista segue.
 */
export const WidgetRenderer = React.memo(function WidgetRenderer({
  widget,
  editable,
  onAdvance,
  onAdjust,
}: Props) {
  switch (widget.type as INTERVIEW_WIDGET) {
    case INTERVIEW_WIDGET.WEEKLY_GRID:
      return (
        <WeeklyGridWidget
          data={widget.data as unknown as WeeklyGridWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    // O roteiro fixo: os cartões que respondem a pergunta por toque.
    case INTERVIEW_WIDGET.CHOICE:
      return (
        <ChoiceWidget
          data={widget.data as unknown as ChoiceWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    case INTERVIEW_WIDGET.ADJUST_MENU:
      return (
        <AdjustMenuWidget
          data={widget.data as unknown as AdjustMenuWidgetData}
          editable={editable}
          onAdjust={onAdjust}
        />
      );
    case INTERVIEW_WIDGET.TEXT:
      return (
        <TextWidget
          data={widget.data as unknown as TextWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    case INTERVIEW_WIDGET.ADDRESS:
      return (
        <AddressWidget
          data={widget.data as unknown as AddressWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    case INTERVIEW_WIDGET.SERVICE_PICKER:
      return (
        <ServicePickerWidget
          data={widget.data as unknown as ServicePickerWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    case INTERVIEW_WIDGET.SERVICE_LIST:
      return (
        <ServiceListWidget
          data={widget.data as unknown as ServiceListWidgetData}
          editable={editable}
          onAdvance={onAdvance}
        />
      );
    case INTERVIEW_WIDGET.SUBSCRIPTION_CARD:
      return (
        <SubscriptionCardWidget
          data={widget.data as unknown as SubscriptionCardWidgetData}
        />
      );
    case INTERVIEW_WIDGET.LOCATION:
      return (
        <LocationWidget data={widget.data as unknown as LocationWidgetData} />
      );
    case INTERVIEW_WIDGET.CLIENT_PREVIEW:
      return (
        <ClientPreviewWidget
          data={widget.data as unknown as ClientPreviewWidgetData}
          editable={editable}
          onAdjust={onAdjust}
        />
      );
    // Fase 2 — os cartões do assistente permanente. `editable` é o que impede
    // confirmar de novo uma proposta que ficou para trás no fio.
    case INTERVIEW_WIDGET.CONFIRMATION:
      return (
        <ConfirmationWidget
          data={widget.data as unknown as ConfirmationWidgetData}
          editable={editable}
        />
      );
    case INTERVIEW_WIDGET.SHORTCUT:
      return (
        <ShortcutWidget data={widget.data as unknown as ShortcutWidgetData} />
      );
    case INTERVIEW_WIDGET.INFO_LIST:
      return (
        <InfoListWidget data={widget.data as unknown as InfoListWidgetData} />
      );
    default:
      return null;
  }
});
